const registerCta = {
  label: 'To Register',
  href: 'https://humanrenaissance.form-wizard.co/users/new?uuid=lLFEODUxFP2Hhs1fdS76dg',
  ariaLabel: 'To Register (opens in a new tab)',
} as const;

export const conventionContent = {
  meta: {
    title: 'The Human Renaissance Forum 2026',
    skipToContent: 'Skip to main content',
  },

  hero: {
    sectionId: 'home',
    logoSrc: '/assets/convention/logo.svg',
    logoAlt: 'The Human Renaissance Forum 2026',
    registerCta,
  },

  nav: {
    label: 'Section navigation',
    openMenuLabel: 'Open navigation menu',
    closeMenuLabel: 'Close navigation menu',
    drawerLabel: 'Site navigation',
    menuIconSrc: '/assets/convention/icon-menu.svg',
    mobileLogoSrc: '/assets/convention/logo-nav-mobile.svg',
    items: [
      { targetId: 'home', label: 'Home' },
      { targetId: 'the-program', label: 'Why Now' },
      { targetId: 'the-forum', label: 'The Gathering' },
      { targetId: 'speakers', label: 'Speakers' },
      { targetId: 'the-venue', label: 'Israel 2026' },
      { targetId: 'participate', label: 'Participate' },
      { targetId: 'about', label: 'About' },
    ],
  },

  theQuestion: {
    sectionId: 'the-question',
    headingId: 'the-question-heading',
    headingLines: ['What kind', 'of humans', 'will the future require?'],
    paragraphGroups: [
      ['Artificial intelligence is redefining human capability.'],
      [
        'A generation is growing up digitally connected — and increasingly alone.',
        'The global order is fracturing faster than institutions can adapt.',
      ],
      ['These are not separate crises.'],
      ['They are the Mission Impossibles of our time.'],
    ],
  },

  whyForum: {
    sectionId: 'the-program',
    headingId: 'the-program-heading',
    headingLines: ['Why This', 'Forum,', 'Why Now'],
    headingLinesMobile: ['Why This Forum,', 'Why Now'],
    subtitle: 'Three domains are reshaping the future of human civilization.',
    accent: 'The Human Renaissance Forum exists to bring these questions into the center of global conversation.',
    cards: [
      {
        iconSrc: '/assets/convention/audience-human-capability.svg',
        iconAlt: '',
        title: 'Human Capability in the Age of AI',
        body: 'In a world where artificial intelligence can replicate skill and knowledge at scale, what remains irreducibly, inimitably human — and how do we educate for it?',
      },
      {
        iconSrc: '/assets/convention/audience-social-fabric.svg',
        iconAlt: '',
        title: 'The Social Fabric of the Next Generation',
        body: 'As the internet reshapes childhood and community, how do we rebuild the social fabric it tore apart — and reconnect a generation that grew up digital but struggles to truly belong?',
      },
      {
        iconSrc: '/assets/convention/audience-leadership.svg',
        iconAlt: '',
        title: 'Leadership in an Age of Disruption',
        body: 'In an era defined by technological acceleration and geopolitical instability — where democracies are under strain, alliances are shifting, and history is accelerating — what is education’s role in preparing leaders for a fractured world?',
      },
    ],
  },

  speakers: {
    sectionId: 'speakers',
    headingId: 'speakers-heading',
    heading: 'The following have confirmed their participation in the 2026 Forum',
    headingLinesDesktop: ['The following', 'have confirmed', 'their participation', 'in the 2026', 'Forum'],
    carouselAriaLabel: 'Confirmed speakers carousel',
    list: [
      {
        name: 'Ilana Dayan',
        title: 'Israel’s leading investigative journalist, host of Uvda',
        photoSrc: '/assets/convention/ilana-dayan.webp',
      },
      {
        name: 'Nas Daily',
        title: 'Creator and entrepreneur, Founder and CEO of Nas Company and Nas Studios',
        photoSrc: '/assets/convention/nas-daily.webp',
      },
      {
        name: 'General Michael Kurilla',
        title: 'Former Commander, US Central Command',
        photoSrc: '/assets/convention/michael-kurilla.webp',
      },
      {
        name: 'Keren Yarhi-Milo',
        title: 'Dean, Columbia School of International and Public Affairs',
        photoSrc: '/assets/convention/keren-yarhi-milo.webp',
      },
      {
        name: 'Jonathan Adiri',
        title: 'Tech entrepreneur, podcaster, influential voice on AI, technology and the economy',
        photoSrc: '/assets/convention/jonathan-adiri.webp',
      },
      {
        name: 'Micah Goodman',
        title: 'Philosopher, bestselling author, leading thinker on AI and the future of humanity',
        photoSrc: '/assets/convention/micah-goodman.webp',
      },
      {
        name: 'Nir Zohar',
        title: 'President of Wix',
        photoSrc: '/assets/convention/nir-zohar.webp',
      },
      {
        name: 'Chaim Broyde',
        title: 'Mayor of Ra’anana',
        photoSrc: '/assets/convention/chaim-broyde.webp',
      },
    ],
    diplomatsCarouselAriaLabel: 'Confirmed diplomats carousel',
    diplomats: [
      {
        name: 'Mohamed Mahmoud Fatehali Abdulla Al Khaja',
        title: 'Representative of the UAE Embassy in Tel Aviv',
        flagSrc: '/assets/convention/flag-uae.png',
        flagAlt: '',
      },
      {
        name: 'Luca Ferrari',
        title: 'Ambassador of Italy to Israel',
        flagSrc: '/assets/convention/flag-italy.png',
        flagAlt: '',
      },
      {
        name: 'Abby Ya-Ping Lee',
        title: 'Ambassador of Taiwan to Israel',
        flagSrc: '/assets/convention/flag-taiwan.png',
        flagAlt: '',
      },
      {
        name: 'Arai Yusuke',
        title: 'Ambassador of Japan',
        flagSrc: '/assets/convention/flag-japan.png',
        flagAlt: '',
      },
    ],
  },

  notAStage: {
    sectionId: 'the-forum',
    headingId: 'the-forum-heading',
    headingLines: ['Not a Stage.', 'A Conversation.'],
    lead: 'The Forum convenes a small, carefully curated group of leaders whose work sits at the intersection of human capability and civilizational change.',
    cards: [
      {
        iconSrc: '/assets/convention/audience-tech-ai.svg',
        iconAlt: '',
        title: 'Technology & AI:',
        body: 'Builders and thinkers shaping the frontier of artificial intelligence and its implications for human capability.',
      },
      {
        iconSrc: '/assets/convention/audience-education.svg',
        iconAlt: '',
        title: 'Education & Human Development:',
        body: 'Leaders rethinking how we develop knowledge, character, and potential in the next generation.',
      },
      {
        iconSrc: '/assets/convention/audience-policy.svg',
        iconAlt: '',
        title: 'Policy & Governance:',
        body: 'Policymakers and advisors navigating the pressures on democratic institutions and global systems.',
      },
      {
        iconSrc: '/assets/convention/audience-business.svg',
        iconAlt: '',
        title: 'Business & Economic Leadership:',
        body: 'Voices shaping how markets, organizations, and work evolve in an age of disruption.',
      },
      {
        iconSrc: '/assets/convention/audience-culture.svg',
        iconAlt: '',
        title: 'Culture & Media:',
        body: 'Writers, journalists, and cultural thinkers shaping how societies understand themselves.',
      },
      {
        iconSrc: '/assets/convention/audience-philosophy.svg',
        iconAlt: '',
        title: 'Philosophy & Social Thought:',
        body: 'Intellectuals exploring deeper questions of meaning, identity, and the trajectory of civilization.',
      },
    ],
  },

  whyParticipate: {
    sectionId: 'why-participate',
    headingId: 'why-participate-heading',
    headingLines: ['Why', 'Participate'],
    paragraphs: [
      {
        text: 'The Forum is taking shape through a series of private conversations with a small group of internationally recognized thinkers and leaders, each operating at the forefront of their field.',
        emphasis: 'normal',
      },
      { text: 'At its center is one question:', emphasis: 'normal' },
      {
        text: 'What kind of humans must we develop for the world ahead?',
        emphasis: 'bold',
      },
      {
        text: 'As a speaker, you join a small, cross-disciplinary group engaging that question across: human capability, education, technology, society and leadership.',
        emphasis: 'normal',
      },
      {
        text: 'This is not a traditional conference setting. It is a space for serious exchange — and for conversations that extend beyond the event itself. At a moment when the answers are no longer obvious to anyone.',
        emphasis: 'normal',
      },
    ],
    registerCta,
  },

  venue: {
    sectionId: 'the-venue',
    headingId: 'the-venue-heading',
    headingLines: ['The', 'Venue'],
    headingLinesMobile: ['The Venue'],
    lead: 'Israel offers a setting equal to the depth and complexity of the question at the heart of the Forum.',
    mapImage: {
      desktopSrc: '/assets/convention/venue-israel.svg',
      mobileSrc: '/assets/convention/venue-israel-mobile.svg',
      alt: '',
    },
    intro:
      'Israel is not only the host of this conversation — it is one of the places where the future is already being tested. Here, the forces shaping the next era intersect: technology and artificial intelligence, questions of identity and social cohesion, and the pressures of geopolitics and leadership under uncertainty.',
    body: {
      before: 'The inaugural convening will be hosted at the ',
      venueName: 'Gabel & Straus Family Campus at AMIT Kfar Batya in Israel.',
      after:
        ' Designed as a living laboratory for the future of learning, the campus positions Israel not only as a host of this conversation, but as proof of its central argument: that a nation serious about the future builds its Education Nation first.',
    },
  },

  expressionOfInterest: {
    sectionId: 'participate',
    headingId: 'participate-heading',
    heading: 'Expression of Interest',
    lead: 'The Forum is currently engaging a select group of speakers, strategic partners, and guests for the inaugural 2026 convening',
    invitation: ['Participation is by invitation. To open a conversation,', 'write to us at'],
    email: 'info@humanrenaissance.org.il',
    cards: [
      {
        iconSrc: '/assets/convention/category-speakers.svg',
        iconAlt: '',
        title: 'Speakers',
        body: 'We are building a small, cross-disciplinary program of 15–20 voices. If you or someone in your network would be a fit, we welcome the conversation.',
      },
      {
        iconSrc: '/assets/convention/category-partners.svg',
        iconAlt: '',
        title: 'Strategic Partners',
        body: 'We are looking for a small number of founding partners to help shape the Forum’s direction, programming, and long-term platform.',
      },
      {
        iconSrc: '/assets/convention/category-guests.svg',
        iconAlt: '',
        title: 'Guests',
        body: 'Attendance is limited and curated. To register interest or recommend a participant, please be in touch directly.',
      },
    ],
  },

  coChairs: {
    headingId: 'co-chairs-heading',
    headingLines: ['Forum', 'Co-chairs'],
    readMoreLabel: 'Read more',
    readLessLabel: 'Read less',
    people: [
      {
        name: 'Shari Safra',
        roleLines: ['AMIT’s National President'],
        photoSrc: '/assets/convention/shari-safra.png',
        photoAlt: 'Shari Safra',
        signatureSrc: '/assets/convention/shari-safra-sign.png',
        quoteParagraphs: [
          '"Every day, across more than 100 schools, we sit with young people who are trying to make sense of a world that is changing faster than anyone - including the adults in their lives- can keep up with. Students whose judgment is being outsourced to an AI. Teenagers who have grown up connected to everything and developed a feel for nothing. And a generation being asked to lead in a world that no longer has a clear map.',
          'We have always believed that education is about developing the whole person. Character. Identity. The capacity to handle a complicated world and make good decisions in it. That conviction is what brought us to this forum.',
          'The question the Human Renaissance Forum is asking - what kind of human beings must we now become - is something we grapple with every day. And we know we’re not the only ones. This is the conversation of our age."',
        ],
      },
      {
        name: 'Yossi Cohen',
        roleLines: ['Head of SoftBank Israel', 'Former head of Mossad'],
        photoSrc: '/assets/convention/yossi-cohen.png',
        photoAlt: 'Yossi Cohen',
        signatureSrc: '/assets/convention/yossi-cohen-sign.png',
        quoteParagraphs: [
          '"I have spent my career at the intersection of intelligence, geopolitics, and the forces that shape history. What I have learned - in negotiating rooms, in high-stakes moments - is that every outcome ultimately depends on the quality of the human being in the room. Technology changes. Alliances shift. What endures is character, judgment, and the capacity to act with clarity under pressure. Those are not innate qualities. They are developed. That is why this forum matters. I have seen what happens when that quality is missing. I have also seen what becomes possible when it is there."',
        ],
      },
    ],
  },

  aboutForum: {
    sectionId: 'about',
    headingId: 'about-heading',
    heading: 'The Human Renaissance Forum',
    summary:
      'The Human Renaissance Forum brings together global leaders to confront the Mission Impossibles of our time — and to rethink how education develops the human capabilities required to meet them.',
    paragraphGroups: [
      [
        'AMIT is Israel’s largest educational network, named the country’s leading network by the Ministry of Education. Across more than 100 schools and tens of thousands of students, AMIT has spent decades developing an approach to education that combines academic excellence with the formation of character, values, and identity.',
      ],
      [
        'Its conviction — that education is the infrastructure of human potential — has made it not only a national leader but a laboratory for what education can become. The new Kfar Batya campus is the physical expression of that ambition: a world-class centre of educational innovation designed to develop, test, and share what the future of learning looks like — for Israel, and for the world.',
        'The Human Renaissance Forum is a natural extension of that mission. A nation that has built an Education Nation hosts the global conversation about why that matters.',
      ],
    ],
  },

  footer: {
    logoSrc: '/assets/convention/logo-footer.svg',
    logoAlt: 'The Human Renaissance Forum 2026',
    phone: {
      label: '+972-35666166',
      href: 'tel:+97235666166',
      ariaLabel: 'Call +972-35666166',
      iconSrc: '/assets/convention/icon-phone.svg',
    },
    email: {
      label: 'info@humanrenaissance.org.il',
      href: 'mailto:info@humanrenaissance.org.il',
      ariaLabel: 'Email info@humanrenaissance.org.il',
      iconSrc: '/assets/convention/icon-mail.svg',
    },
  },
} as const;

export type NavItem = (typeof conventionContent.nav.items)[number];
